// ──────────────────────────────────────────────────────
// ProductCardLegacy — compact dark variant of the unified product card.
//
// QCARE-AI-DEV-FRONT-UNIFY-CARDS (2026-05-09): rebuilt to match the
// dark premium design system used by HeroCard + BudgetCard.
//   • Same dark navy/teal gradient shell + subtle cyan border + soft
//     premium shadow.
//   • Same CSS Grid layout (`minmax(<image col>, 24%) 1fr`) — image
//     LEFT in a cream panel with `object-fit: contain`, text RIGHT.
//   • White title, muted brand, cyan price + smaller `ريال` suffix.
//   • Single full-width green emerald `<TrustBar size="sm" />` footer
//     spanning both grid columns via `gridColumn: '1 / -1'`.
//
// Visual differences vs HeroCard / BudgetCard:
//   • No top header row — routine / compare / fallback layouts don't
//     surface a hero/budget label, so the badge slot is omitted.
//   • Smaller image column (`minmax(80px, 24%)`) and shorter image
//     panel (`clamp(90px, 16vw, 130px)`) so multi-card routine layouts
//     stay compact.
//   • Optional `shortReasonAr` from backend kept as a single-line
//     reason annotation under the price (no divider, no header).
//
// CardRenderer dispatch is unchanged — this component is still routed
// to for `layout === 'legacy_multi'`, for `NEXT_PUBLIC_PREMIUM_CARDS=0`
// rollback, and for fallback when product.role is unset/default.
// ──────────────────────────────────────────────────────
import Link from 'next/link';
import type { ProductSuggestion } from '@/lib/types';
import { TrustBar } from './TrustBar';
import { isProductOutOfStock, OUT_OF_STOCK_LABEL_AR } from './availability-badge';

const CYAN_PRIMARY = '#35E7F2';
const CYAN_SOFT = '#a8dce4';
const TEXT_PRIMARY = '#ffffff';
const TEXT_MUTED = 'rgba(255,255,255,0.55)';
const SHELL_BORDER = 'rgba(80, 200, 200, 0.32)';

export function ProductCardLegacy({ product }: { product: ProductSuggestion }) {
    const ps = product;
    const outOfStock = isProductOutOfStock(ps);

    return (
        <Link
            href={ps.href}
            style={{
                display: 'block',
                borderRadius: 16,
                overflow: 'hidden',
                cursor: 'pointer',
                textDecoration: 'none',
                background:
                    'radial-gradient(circle at 88% 10%, rgba(80, 200, 200, 0.14), transparent 36%), '
                    + 'linear-gradient(145deg, #132633 0%, #0E1A24 45%, #071016 100%)',
                border: `1px solid ${SHELL_BORDER}`,
                boxShadow: '0 6px 18px rgba(0,0,0,0.40), inset 0 1px 0 rgba(255,255,255,0.04)',
            }}
        >
            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(80px, 24%) 1fr',
                    alignItems: 'stretch',
                    direction: 'ltr',
                    minHeight: 110,
                }}
            >
                {/* Image panel — LEFT, top-anchored, cream surface, contain. */}
                <div
                    style={{
                        padding: 8,
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'center',
                    }}
                >
                    <div
                        style={{
                            background: '#F3F7F8',
                            borderRadius: 14,
                            padding: 10,
                            width: '100%',
                            height: 'clamp(90px, 16vw, 130px)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: 'inset 0 0 0 1px rgba(80,200,200,0.10)',
                        }}
                    >
                        {/* eslint-disable-next-line @next/next/no-img-element */}
                        <img
                            src={ps.imageUrl}
                            alt={ps.nameAr}
                            className="q-card-image-fade"
                            style={{
                                maxWidth: '100%',
                                maxHeight: '100%',
                                objectFit: 'contain',
                            }}
                            // QCARE-AI-DEV-MOBILE-CHAT-ROUTINE-BLOCK-REVEAL-PHASE-1-5
                            // (2026-05-04): images start at opacity 0 (via the
                            // `.q-card-image-fade` class) and flip to opacity 1
                            // on load. The class also defines the 380ms ease
                            // transition + reduced-motion guard.
                            onLoad={(e) => {
                                const el = e.target as HTMLImageElement;
                                if (el.dataset.fallback === '1') return;
                                el.style.opacity = '1';
                            }}
                            // External URLs that hotlink-block render as a
                            // broken icon by default. Swap to the QIO logo
                            // placeholder so the card stays visually clean.
                            onError={(e) => {
                                const el = e.target as HTMLImageElement;
                                if (el.dataset.fallback === '1') return;
                                el.dataset.fallback = '1';
                                el.src = '/branding/qio-logo.jpeg';
                                el.style.opacity = '0.55';
                            }}
                        />
                    </div>
                </div>

                {/* Text panel — RTL flow. Optional OOS pill at the top, then
                    title / brand / price / optional one-line reason. No
                    badge row (routine / compare context isn't a hero/budget
                    pick). */}
                <div
                    style={{
                        direction: 'rtl',
                        padding: '10px 12px 10px 6px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        minWidth: 0,
                        gap: 4,
                    }}
                >
                    {outOfStock && (
                        <span
                            style={{
                                alignSelf: 'flex-start',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 4,
                                padding: '2px 6px',
                                borderRadius: 6,
                                fontSize: 10,
                                fontWeight: 600,
                                background: 'rgba(180,35,24,0.15)',
                                color: '#fca5a5',
                                border: '1px solid rgba(252,165,165,0.40)',
                            }}
                            aria-label={OUT_OF_STOCK_LABEL_AR}
                        >
                            {OUT_OF_STOCK_LABEL_AR}
                        </span>
                    )}

                    <div
                        style={{
                            color: TEXT_PRIMARY,
                            fontWeight: 600,
                            fontSize: 14,
                            lineHeight: 1.35,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                        }}
                    >
                        {ps.nameAr}
                    </div>

                    {ps.brandAr && (
                        <div style={{ color: TEXT_MUTED, fontSize: 11, fontWeight: 500 }}>
                            {ps.brandAr}
                        </div>
                    )}

                    {ps.price != null && ps.price > 0 && (
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 5, marginTop: 1 }}>
                            <span
                                style={{
                                    fontSize: 18,
                                    fontWeight: 700,
                                    color: CYAN_PRIMARY,
                                    letterSpacing: '0.01em',
                                    textShadow: '0 1px 6px rgba(53,231,242,0.22)',
                                    lineHeight: 1,
                                }}
                            >
                                {ps.price.toFixed(2)}
                            </span>
                            <span style={{ fontSize: 11, color: CYAN_SOFT, fontWeight: 500 }}>ريال</span>
                        </div>
                    )}

                    {ps.shortReasonAr && (
                        <div
                            style={{
                                color: 'rgba(255,255,255,0.78)',
                                fontSize: 11,
                                lineHeight: 1.4,
                                marginTop: 2,
                                display: '-webkit-box',
                                WebkitLineClamp: 1,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                            }}
                        >
                            {ps.shortReasonAr}
                        </div>
                    )}
                </div>

                {/* QCARE-AI-DEV-FRONT-UNIFY-CARDS (2026-05-09): full-width
                    trust bar footer spanning both columns. Same green
                    emerald TrustBar used by HeroCard / BudgetCard so the
                    authenticity surface is identical across all three card
                    variants. `direction: 'rtl'` re-applied for clean RTL
                    alignment. */}
                <div
                    style={{
                        gridColumn: '1 / -1',
                        padding: '0 8px 8px 8px',
                        direction: 'rtl',
                    }}
                >
                    <TrustBar size="sm" />
                </div>
            </div>
        </Link>
    );
}
